import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = guest, obj = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Returning from Google OAuth: let AuthCallback establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.error("Logout request failed:", e);
    }
    setUser(false);
  };

  const updateProfile = async (payload) => {
    const { data } = await api.patch("/profile", payload);
    setUser(data);
    return data;
  };

  const uploadPhoto = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/profile/photo", fd);
    setUser(data);
    return data;
  };

  const uploadPortfolio = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/profile/portfolio", fd);
    setUser(data);
    return data;
  };

  const removePortfolio = async (fileId) => {
    const { data } = await api.delete(`/profile/portfolio/${fileId}`);
    setUser(data);
    return data;
  };

  const removePhoto = async () => {
    const { data } = await api.delete("/profile/photo");
    setUser(data);
    return data;
  };

  const uploadCasting = async (file, category) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", category);
    const { data } = await api.post("/profile/casting", fd);
    setUser(data);
    return data;
  };

  const removeCasting = async (fileId) => {
    const { data } = await api.delete(`/profile/casting/${fileId}`);
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider
      value={{
        user, setUser, loading, login, register, logout, checkAuth, formatApiErrorDetail,
        updateProfile, uploadPhoto, uploadPortfolio, removePortfolio,
        removePhoto, uploadCasting, removeCasting,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
